import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { HeartIcon, MessageCircleIcon, PauseIcon, PlayIcon, SendIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
import { brand, reelClips } from '../data/content';
const CLIP_MS = 4200;
export function HeroReel() {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [liked, setLiked] = useState(false);
  const [progress, setProgress] = useState(0);
  const startedAt = useRef<number>(0);
  const clip = reelClips[index];
  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    startedAt.current = performance.now() - progress * CLIP_MS;
    const tick = (now: number) => {
      const ratio = Math.min((now - startedAt.current) / CLIP_MS, 1);
      setProgress(ratio);
      if (ratio >= 1) {
        setProgress(0);
        setIndex(i => (i + 1) % reelClips.length);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, index]);
  const goTo = (i: number) => {
    setProgress(0);
    setIndex(i);
  };
  return <div className="relative mx-auto w-full max-w-[19rem] sm:max-w-[21rem]" data-id="element-144" data-mp="3m753s694v43z">
      <div className="pearl-surface absolute -inset-3 -z-10 rounded-[2.75rem] border border-butter-100 dark:border-butter-700/40" data-id="element-145" data-mp="dpr40xxxtw4d" />

      <div className="relative aspect-[9/16] w-full overflow-hidden rounded-[2rem] bg-slate-900" data-id="element-146" data-mp="hsne209ewjwp">
        <AnimatePresence initial={false} data-id="element-147" data-mp="3xl9phk7r2m0">
          <motion.img key={clip.id} src={clip.src} alt={clip.alt} initial={{
          opacity: 0,
          scale: reduceMotion ? 1 : 1.08
        }} animate={{
          opacity: 1,
          scale: reduceMotion ? 1 : 1
        }} exit={{
          opacity: 0
        }} transition={{
          opacity: {
            duration: 0.5
          },
          scale: {
            duration: CLIP_MS / 1000 + 1,
            ease: 'linear'
          }
        }} className="absolute inset-0 h-full w-full object-cover" loading="eager" data-id="element-148" data-mp="8xh4cq70thow" />
        </AnimatePresence>

        {/* Legibility scrim */}
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/10 to-slate-950/45" data-id="element-149" data-mp="web0js3380iq" />

        {/* Progress bars */}
        <div className="absolute inset-x-3 top-3 flex gap-1.5" data-id="element-150" data-mp="sdr64w9wfb0a">
          {reelClips.map((c, i) => <button key={c.id} type="button" onClick={() => goTo(i)} aria-label={`Play clip ${i + 1}: ${c.caption}`} className="group h-1.5 flex-1 rounded-full bg-white/30" data-id="element-151" data-mp="nj4rmk8it2bz" data-mpi={i}>
              <span className="block h-full rounded-full bg-butter-300 transition-[width] duration-100 ease-linear" style={{
            width: i < index ? '100%' : i === index ? `${progress * 100}%` : '0%'
          }} data-id="element-152" data-mp="dol6j775firv" />
            </button>)}
        </div>

        {/* Account row */}
        <div className="absolute inset-x-4 top-8 flex items-center gap-2.5" data-id="element-153" data-mp="2g8nm6r61l2k8">
          <span className="pearl-surface flex h-8 w-8 flex-none items-center justify-center rounded-full border border-butter-200/70" data-id="element-154" data-mp="2kar6846s1fh4">
            <span className="font-display text-xs font-semibold text-slate-800" data-id="element-155" data-mp="2pawr91ijk3q8">ee</span>
          </span>
          <span className="truncate text-sm font-semibold text-white drop-shadow" data-id="element-156" data-mp="2u8ow0w4abkbu">
            {brand.name}
          </span>
        </div>

        {/* Side actions */}
        <div className="absolute bottom-24 right-3 flex flex-col items-center gap-4" data-id="element-157" data-mp="21k12kd9dton7">
          <button type="button" onClick={() => setLiked(v => !v)} aria-pressed={liked} aria-label={liked ? 'Unlike this clip' : 'Like this clip'} className="flex flex-col items-center gap-1 text-white transition-transform duration-200 hover:scale-110" data-id="element-158" data-mp="26jdql1bzp1rd">
            <HeartIcon className={`h-6 w-6 drop-shadow ${liked ? 'fill-butter-300 text-butter-300' : ''}`} data-id="element-159" data-mp="1dt44o4ef8ruy" />
            <span className="text-[11px] font-semibold drop-shadow" data-id="element-160" data-mp="1iryjkv99pw9s">Like</span>
          </button>
          <span className="flex flex-col items-center gap-1 text-white" data-id="element-161" data-mp="3kh4fxp3fl4bm">
            <MessageCircleIcon className="h-6 w-6 drop-shadow" aria-hidden="true" data-id="element-162" data-mp="3gjgjuiq055i5" />
            <span className="text-[11px] font-semibold drop-shadow" data-id="element-163" data-mp="3ua2348jdbhgk">Comment</span>
          </span>
          <span className="flex flex-col items-center gap-1 text-white" data-id="element-164" data-mp="3phin74x7ka8l">
            <SendIcon className="h-6 w-6 drop-shadow" aria-hidden="true" data-id="element-165" data-mp="81kbrfevdsu9" />
            <span className="text-[11px] font-semibold drop-shadow" data-id="element-166" data-mp="32zbxh3j5b6c">Share</span>
          </span>
        </div>

        {/* Caption */}
        <div className="absolute inset-x-4 bottom-4" data-id="element-167" data-mp="h30ed0zg0qnx">
          <AnimatePresence mode="wait" initial={false} data-id="element-168" data-mp="h0e60z4qa74n">
            <motion.p key={clip.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.3
          }} className="max-w-[15rem] text-base font-semibold leading-snug text-white drop-shadow" data-id="element-169" data-mp="c4qdqauy8xux">
              {clip.caption}
            </motion.p>
          </AnimatePresence>

          <div className="mt-3 flex items-center gap-2" data-id="element-170" data-mp="ye6yy0yyjhbp">
            <button type="button" onClick={() => setPlaying(v => !v)} aria-label={playing ? 'Pause reel' : 'Play reel'} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur transition-colors hover:bg-white/30" data-id="element-171" data-mp="klh8nkcebq8s">
              {playing ? <PauseIcon className="h-4 w-4" data-id="element-172" data-mp="flbd3t309jlf" /> : <PlayIcon className="h-4 w-4" data-id="element-173" data-mp="fme4e248po1v" />}
            </button>
            <button type="button" onClick={() => setMuted(v => !v)} aria-label={muted ? 'Unmute reel' : 'Mute reel'} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur transition-colors hover:bg-white/30" data-id="element-174" data-mp="1mbqss2ytgsf">
              {muted ? <VolumeXIcon className="h-4 w-4" data-id="element-175" data-mp="1vy06iwh0tndv" /> : <Volume2Icon className="h-4 w-4" data-id="element-176" data-mp="201pwnqdcgd10" />}
            </button>
          </div>
        </div>
      </div>
    </div>;
}